import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { Copy, Trash2, RefreshCw, Plus, Users, Key, UserPlus, HelpCircle } from 'lucide-react';

const CustomerManager = ({ onShowHelp }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    user_id: ''
  });
  const [copySuccess, setCopySuccess] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    usage_limit: '',
    claude_system_prompt_override: '',
    webhook_url: ''
  });

  // Check if user is admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is authenticated and is admin
    const checkAdminStatus = async () => {
      try {
        console.log('🔍 Checking admin status...');
        const response = await axios.get(getApiUrl('/user/profile'), {
          withCredentials: true
        });
        
        console.log('📊 Profile response:', response.data);
        
        if (response.data.user && response.data.user.is_admin) {
          console.log('✅ User is admin');
          setIsAdmin(true);
          setUser(response.data.user);
          fetchCustomers();
          fetchUsers();
        } else {
          console.log('❌ User is not admin');
          setError('Admin privileges required');
          setLoading(false);
        }
      } catch (err) {
        console.error('❌ Error checking admin status:', err);
        console.error('❌ Error response:', err.response?.data);
        setError('Authentication required');
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/admin/customers'), {
        withCredentials: true
      });
      setCustomers(response.data.customers || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err.response?.data?.error || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(getApiUrl('/admin/users'), {
        withCredentials: true
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      // Fallback to mock data if API fails
      setUsers([
        { id: 1, username: 'admin', email: 'admin@enable.ai' },
        { id: 2, username: 'user1', email: 'user1@example.com' },
        { id: 3, username: 'user2', email: 'user2@example.com' }
      ]);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(getApiUrl('/admin/customers'), formData, {
        withCredentials: true
      });
      
      // Refresh the customers list to get the updated structure
      fetchCustomers();
      setFormData({ name: '', user_id: '' });
      setShowCreateForm(false);
      setError(null);
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.delete(getApiUrl(`/admin/customers/${customerId}`), {
        withCredentials: true
      });
      
      setCustomers(customers.filter(c => c.customer_id !== customerId));
      setError(null);
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(err.response?.data?.error || 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateApiKey = async (customerId) => {
    if (!window.confirm('Are you sure you want to regenerate the API key? This will invalidate the current key.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(getApiUrl(`/admin/customers/${customerId}/regenerate`), {}, {
        withCredentials: true
      });
      
              setCustomers(customers.map(c => 
          c.customer_id === customerId ? response.data.customer : c
        ));
      setError(null);
    } catch (err) {
      console.error('Error regenerating API key:', err);
      setError(err.response?.data?.error || 'Failed to regenerate API key');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditFormData({
      name: customer.customer_name,
      usage_limit: customer.usage_limit || '',
      claude_system_prompt_override: customer.claude_system_prompt_override || '',
      webhook_url: customer.webhook_url || ''
    });
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.put(getApiUrl(`/admin/customers/${editingCustomer.id}`), editFormData, {
        withCredentials: true
      });
      
              setCustomers(customers.map(c => 
          c.customer_id === editingCustomer.customer_id ? response.data.customer : c
        ));
      setEditingCustomer(null);
      setEditFormData({ name: '', usage_limit: '', claude_system_prompt_override: '', webhook_url: '' });
      setError(null);
    } catch (err) {
      console.error('Error updating customer:', err);
      setError(err.response?.data?.error || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCustomer(null);
    setEditFormData({ name: '', usage_limit: '', claude_system_prompt_override: '', webhook_url: '' });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('API key copied!');
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('Failed to copy');
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-900 border border-red-700 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-red-300 mb-2">Admin Access Required</h2>
          <p className="text-red-300">
            This page requires admin privileges. Only authorized administrators can access the Customer Manager.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Customer Manager</h1>
            <p className="text-gray-400">Manage customer accounts and API keys</p>
          </div>
          <div className="flex space-x-3">
            {onShowHelp && (
              <button
                onClick={onShowHelp}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Help</span>
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center space-x-2 bg-[#7B61FF] text-white px-4 py-2 rounded-xl hover:bg-[#6B51EF] transition-colors duration-300"
            >
              <Plus className="w-4 h-4" />
              <span>Add Customer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900 border border-red-700 rounded-xl p-4 mb-6">
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {copySuccess && (
        <div className="bg-green-900 border border-green-700 rounded-xl p-4 mb-6">
          <p className="text-green-300">{copySuccess}</p>
        </div>
      )}

      {/* Create Customer Form */}
      {showCreateForm && (
        <div className="bg-gray-800 border border-gray-600 rounded-xl p-6 mb-6" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Create New Customer</h3>
          <form onSubmit={handleCreateCustomer}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                  placeholder="Enter customer name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Link to User (Optional)
                </label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
                >
                  <option value="">Select a user (optional)</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-[#7B61FF] text-white px-4 py-2 rounded-xl hover:bg-[#6B51EF] disabled:opacity-50 transition-colors duration-300"
              >
                {loading ? 'Creating...' : 'Create Customer'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-700 text-gray-300 px-4 py-2 rounded-xl hover:bg-gray-600 transition-colors duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Customers Table */}
      <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Customers</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7B61FF] mx-auto"></div>
            <p className="text-gray-400 mt-2">Loading customers...</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="p-6 text-center">
            <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No customers found. Create your first customer to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    API Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Users & Agents
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 divide-y divide-gray-700">
                {customers.map((customer) => (
                  <tr key={customer.customer_id} className="hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-white flex items-center">
                          {customer.customer_name}
                          {customer.claude_system_prompt_override && (
                            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-900 text-purple-300">
                              Custom Prompt
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-400">ID: {customer.customer_id}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Usage: {customer.usage_count || 0}
                          {customer.usage_limit && ` / ${customer.usage_limit}`}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1">
                          <div className="text-sm font-mono text-white bg-gray-800 px-2 py-1 rounded">
                            {customer.api_key.substring(0, 20)}...
                          </div>
                        </div>
                        <button
                          onClick={() => copyToClipboard(customer.api_key)}
                          className="text-gray-400 hover:text-gray-300 transition-colors duration-300"
                          title="Copy API key"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        {/* Users Section */}
                        {customer.users && customer.users.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-300 mb-2">Users ({customer.total_users})</div>
                            {customer.users.map((user, userIndex) => (
                              <div key={user.user_id} className="ml-4 mb-3 p-2 bg-gray-800 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="text-sm text-white font-medium">
                                    {user.email}
                                    {user.is_admin && (
                                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-900 text-blue-300">
                                        Admin
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Credits: {user.credits}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-400 mb-2">
                                  Last login: {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                                </div>
                                
                                {/* Agents for this user */}
                                {user.agents && user.agents.length > 0 && (
                                  <div className="ml-4">
                                    <div className="text-xs font-medium text-gray-400 mb-1">Agents ({user.agents.length})</div>
                                    {user.agents.map((agent, agentIndex) => (
                                      <div key={agent.agent_id} className="ml-2 mb-1 p-1 bg-gray-700 rounded text-xs">
                                        <div className="flex items-center justify-between">
                                          <span className="text-white font-medium">{agent.agent_name}</span>
                                          <span className="text-gray-400">{agent.llm}</span>
                                        </div>
                                        <div className="text-gray-500">
                                          {agent.agent_type} • Created: {new Date(agent.created_at).toLocaleDateString()}
                                          {agent.healing_recommended && (
                                            <span className="ml-2 text-orange-400">⚠️ Healing Recommended</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Orphaned Agents */}
                        {customer.orphaned_agents && customer.orphaned_agents.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-300 mb-2">Orphaned Agents ({customer.orphaned_agents.length})</div>
                            {customer.orphaned_agents.map((agent, agentIndex) => (
                              <div key={agent.agent_id} className="ml-4 mb-1 p-1 bg-gray-700 rounded text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-white font-medium">{agent.agent_name}</span>
                                  <span className="text-gray-400">{agent.llm}</span>
                                </div>
                                <div className="text-gray-500">
                                  {agent.agent_type} • Created: {new Date(agent.created_at).toLocaleDateString()}
                                  {agent.healing_recommended && (
                                    <span className="ml-2 text-orange-400">⚠️ Healing Recommended</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* No users or agents */}
                        {(!customer.users || customer.users.length === 0) && 
                         (!customer.orphaned_agents || customer.orphaned_agents.length === 0) && (
                          <div className="text-xs text-gray-500 italic">No users or agents found</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditCustomer(customer)}
                          className="text-green-400 hover:text-green-300 transition-colors duration-300"
                          title="Edit customer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRegenerateApiKey(customer.customer_id)}
                          className="text-[#7B61FF] hover:text-[#6B51EF] transition-colors duration-300"
                          title="Regenerate API key"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCustomer(customer.customer_id)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-300"
                          title="Delete customer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">How it works</h3>
        <div className="space-y-2 text-sm text-blue-800">
          <p>• <strong>Create customers</strong> to generate unique API keys for accessing the feedback system</p>
          <p>• <strong>Link customers to users</strong> to automatically send onboarding emails with API credentials</p>
          <p>• <strong>Regenerate API keys</strong> if they become compromised or need to be rotated</p>
          <p>• <strong>Monitor usage</strong> through the Customer Feedback Dashboard</p>
          <p>• <strong>Custom Claude prompts</strong> allow customers to define their own evaluation style</p>
        </div>
      </div>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Customer: {editingCustomer.customer_name}</h3>
              <button 
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleUpdateCustomer}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usage Limit
                  </label>
                  <input
                    type="number"
                    value={editFormData.usage_limit}
                    onChange={(e) => setEditFormData({ ...editFormData, usage_limit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty for unlimited"
                  />
                  <p className="text-sm text-gray-500 mt-1">Current usage: {editingCustomer.usage_count || 0}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Claude System Prompt
                  </label>
                  <textarea
                    value={editFormData.claude_system_prompt_override}
                    onChange={(e) => setEditFormData({ ...editFormData, claude_system_prompt_override: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty to use default Claude evaluation prompt..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This custom prompt will be used instead of the default Claude evaluation system message. 
                    Useful for customers who want specific evaluation criteria or tone.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={editFormData.webhook_url}
                    onChange={(e) => setEditFormData({ ...editFormData, webhook_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter a webhook URL to receive real-time updates"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    If provided, this URL will receive a POST request with the customer's usage data.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Updating...' : 'Update Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManager; 